/* The Slow Pour: local, dependency-free scroll choreography. */
(() => {
  if (window.SlowPour) {
    window.SlowPour.refresh();
    return;
  }

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  let config;
  let stages = [];
  let flights = [];
  let paused = false;
  let observer;
  let ring;
  let targetY = window.scrollY;
  let wheeling = false;
  let lastTime = 0;
  let pointer = { x: -100, y: -100, currentX: -100, currentY: -100 };

  try {
    paused = sessionStorage.getItem('slow-pour-paused') === 'true';
  } catch (error) {
    // Storage is optional; the control still works without it.
  }

  const enabled = () => config && !paused && !reduced.matches;
  const option = (name) => config && config.dataset[name] === 'true';
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  function syncControl() {
    document.querySelectorAll('[data-pour-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(paused));
      const label = button.querySelector('[data-toggle-label]');
      if (label) label.textContent = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
    });
    root.classList.toggle('pour-motion-paused', paused || reduced.matches);
    if (ring) ring.hidden = !enabled() || !fine.matches || !option('cursor');
  }

  function measureFlight(flight) {
    flight.element.classList.remove('pour-flight--active');
    flight.element.style.height = '';
    flight.track.style.transform = '';
    flight.viewport.scrollLeft = 0;
    flight.distance = Math.max(0, flight.track.scrollWidth - flight.viewport.clientWidth);
    flight.active = enabled() && flight.element.dataset.scrollEnabled === 'true' && flight.distance > 0;
    if (flight.active) {
      flight.element.classList.add('pour-flight--active');
      flight.runway = flight.distance;
      flight.element.style.height = `${flight.sticky.offsetHeight + flight.runway}px`;
    }
  }

  function navigateFlight(flight, direction, focusedCard) {
    const cards = Array.from(flight.track.children);
    const width = cards[0] ? cards[0].getBoundingClientRect().width + 24 : 1;
    const current = flight.active ? flight.position / width : flight.viewport.scrollLeft / width;
    const index = focusedCard ? cards.indexOf(focusedCard) : clamp(Math.round(current) + direction, 0, cards.length - 1);
    const left = Math.min(index * width, flight.distance);
    wheeling = false;
    if (flight.active) {
      const top = parseFloat(getComputedStyle(flight.sticky).top) || 0;
      const destination = window.scrollY + flight.element.getBoundingClientRect().top - top + left;
      targetY = destination;
      window.scrollTo({ top: destination, behavior: reduced.matches ? 'instant' : 'smooth' });
    } else {
      flight.viewport.scrollTo({ left, behavior: reduced.matches ? 'instant' : 'smooth' });
    }
  }

  function refresh() {
    config = document.querySelector('[data-pour-experience]');
    if (!config) return;
    stages = Array.from(document.querySelectorAll('[data-pour-stage]')).map((element) => ({
      element,
      image: element.querySelector('[data-pour-image]'),
      inset: element.querySelector('[data-pour-inset]'),
      word: element.querySelector('.pour-stage__underword'),
      progress: 0
    }));
    flights = Array.from(document.querySelectorAll('[data-pour-flight]')).map((element) => ({
      element,
      sticky: element.querySelector('.pour-flight__sticky'),
      viewport: element.querySelector('.pour-flight__viewport'),
      track: element.querySelector('.pour-flight__track'),
      progress: element.querySelector('.pour-flight__progress span'),
      count: element.querySelector('[data-flight-count]'),
      position: 0,
      distance: 0,
      runway: 0
    }));
    flights.forEach((flight) => {
      measureFlight(flight);
      flight.element.querySelector('[data-flight-prev]').onclick = () => navigateFlight(flight, -1);
      flight.element.querySelector('[data-flight-next]').onclick = () => navigateFlight(flight, 1);
      flight.track.onfocusin = (event) => {
        const card = event.target.closest('.pour-flight__card');
        if (card) navigateFlight(flight, 0, card);
      };
    });

    if (observer) observer.disconnect();
    root.classList.toggle('pour-reveals-on', enabled() && option('reveals'));
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('pour-settled');
        else if (entry.boundingClientRect.top > window.innerHeight) entry.target.classList.remove('pour-settled');
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -12px 0px' });
    document.querySelectorAll('[data-pour-reveal]').forEach((element) => observer.observe(element));

    if (!ring && option('cursor')) {
      ring = document.createElement('div');
      ring.className = 'pour-cursor';
      ring.setAttribute('aria-hidden', 'true');
      document.body.append(ring);
    }
    document.querySelectorAll('[data-pour-toggle]').forEach((button) => {
      button.onclick = () => {
        paused = !paused;
        wheeling = false;
        targetY = window.scrollY;
        try { sessionStorage.setItem('slow-pour-paused', String(paused)); } catch (error) { /* Optional storage. */ }
        refresh();
      };
    });
    syncControl();
  }

  function nestedScroller(target) {
    let element = target instanceof Element ? target : target.parentElement;
    if (element?.closest('input, textarea, select, [contenteditable], [role="dialog"], .drawer, [data-drawer], .lightbox')) return true;
    if (getComputedStyle(document.body).overflow === 'hidden' || getComputedStyle(root).overflow === 'hidden') return true;
    while (element && element !== document.body && element !== root) {
      const style = getComputedStyle(element);
      if (/(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight + 2) return true;
      element = element.parentElement;
    }
    return false;
  }

  window.addEventListener('wheel', (event) => {
    if (!enabled() || !option('smooth') || !fine.matches || window.Shopify?.designMode) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY) || nestedScroller(event.target)) return;
    const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (!wheeling) targetY = window.scrollY;
    const next = clamp(targetY + event.deltaY * factor, 0, max);
    if (next === window.scrollY && (next === 0 || next === max)) return;
    event.preventDefault();
    targetY = next;
    wheeling = true;
  }, { passive: false });

  const cancelWheel = () => { wheeling = false; targetY = window.scrollY; };
  window.addEventListener('touchstart', cancelWheel, { passive: true });
  window.addEventListener('keydown', (event) => {
    if (['Tab', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) cancelWheel();
  });
  window.addEventListener('pointerdown', (event) => {
    cancelWheel();
    if (!enabled() || !option('ripples') || event.button > 0) return;
    if (event.target.closest('input, textarea, select, [contenteditable]')) return;
    const ripple = document.createElement('span');
    ripple.className = 'pour-ripple';
    ripple.style.left = `${event.clientX}px`;
    ripple.style.top = `${event.clientY}px`;
    ripple.setAttribute('aria-hidden', 'true');
    document.body.append(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
    setTimeout(() => ripple.remove(), 1200);
  }, { passive: true });
  window.addEventListener('pointermove', (event) => {
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    if (ring) ring.classList.toggle('pour-cursor--link', Boolean(event.target.closest('a, button, [role="button"]')));
  }, { passive: true });
  document.addEventListener('mouseleave', () => { if (ring) ring.style.opacity = '0'; });
  document.addEventListener('mouseenter', () => { if (ring) ring.style.opacity = '1'; });

  function tick(time) {
    const dt = Math.min(40, time - (lastTime || time - 16));
    lastTime = time;
    const ease = 1 - Math.exp(-dt / 90);
    if (wheeling) {
      const next = window.scrollY + (targetY - window.scrollY) * ease;
      window.scrollTo({ top: next, behavior: 'instant' });
      if (Math.abs(targetY - next) < 0.7) {
        window.scrollTo({ top: targetY, behavior: 'instant' });
        wheeling = false;
      }
    }
    if (enabled()) {
      if (ring && fine.matches && option('cursor')) {
        pointer.currentX += (pointer.x - pointer.currentX) * ease;
        pointer.currentY += (pointer.y - pointer.currentY) * ease;
        ring.style.transform = `translate3d(${pointer.currentX}px, ${pointer.currentY}px, 0)`;
      }
      if (option('parallax')) {
        const distance = Number(config.dataset.intensity) || 32;
        stages.forEach((stage) => {
          const rect = stage.element.getBoundingClientRect();
          if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;
          const progress = clamp((window.innerHeight - rect.top) / (window.innerHeight + rect.height), 0, 1);
          stage.progress += (progress - stage.progress) * ease;
          const drift = (stage.progress - 0.4) * distance;
          if (stage.image) {
            stage.image.style.transform = `translate3d(0, ${-drift}px, 0)`;
            const inset = Math.max(0, 0.28 - stage.progress) * 12;
            stage.image.style.clipPath = `inset(${inset}% ${inset}% 0 ${inset}%)`;
          }
          if (stage.inset) stage.inset.style.transform = `translate3d(0, ${drift * 1.4}px, 0)`;
          if (stage.word) stage.word.style.transform = `translate3d(${-drift * .7}px, ${drift * .4}px, 0)`;
        });
      }
    } else {
      stages.forEach((stage) => {
        [stage.image, stage.inset, stage.word].forEach((element) => {
          if (element) { element.style.transform = ''; element.style.clipPath = ''; }
        });
      });
    }
    flights.forEach((flight) => {
      let progress;
      if (flight.active) {
        const top = parseFloat(getComputedStyle(flight.sticky).top) || 0;
        const progressTarget = clamp((top - flight.element.getBoundingClientRect().top) / flight.runway, 0, 1);
        flight.position += (progressTarget * flight.distance - flight.position) * ease;
        flight.track.style.transform = `translate3d(${-flight.position}px, 0, 0)`;
        progress = flight.position / flight.distance;
      } else {
        progress = flight.distance ? flight.viewport.scrollLeft / flight.distance : 0;
      }
      if (flight.progress) flight.progress.style.transform = `scaleX(${.1 + .9 * progress})`;
      if (flight.count) {
        const count = flight.track.children.length;
        flight.count.textContent = `${String(Math.round(progress * (count - 1)) + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;
      }
    });
    requestAnimationFrame(tick);
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { flights.forEach(measureFlight); cancelWheel(); }, 180);
  });
  reduced.addEventListener('change', refresh);
  fine.addEventListener('change', syncControl);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', () => setTimeout(refresh, 0));
  window.SlowPour = { refresh };
  refresh();
  window.addEventListener('load', () => flights.forEach(measureFlight), { once: true });
  requestAnimationFrame(tick);
})();
